import { CrudPage } from '../../components/CrudPage';
import { type DeliveryDto, DeliveryControllerService } from '../../api';
import DeliveryFormDialog from '../forms/DeliveryFormDialog.tsx';
import { useTranslation } from 'react-i18next';

// Type with required id for the CrudPage
type DeliveryWithId = DeliveryDto & { id: string };

const DeliveriesPage = () => {
  const { t, i18n } = useTranslation(['common']);

  const fetchData = async (): Promise<DeliveryWithId[]> => {
    const deliveries = await DeliveryControllerService.findAll();
    return deliveries.map(delivery => ({
      ...delivery,
      id: delivery.uuid || ''
    }));
  };

  // Custom renderer for date fields
  const dateRenderer = (data: DeliveryWithId, field: 'expectedDate' | 'actualDate') => {
    const dateValue = data[field];
    if (!dateValue) return '';
    const date = new Date(dateValue);
    return date.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  // Custom renderer for status field
  const statusRenderer = (data: DeliveryWithId) => {
    if (!data.status) return '';
    return t(`delivery.status.${data.status}`);
  };

  return (
    <CrudPage<DeliveryWithId>
      titleKey="routes.deliveries"
      gridMeta={[
        { field: 'materialName', headerKey: 'delivery.grid.field.material', width: 150 },
        { field: 'quantity', headerKey: 'delivery.grid.field.quantity', width: 100, align: 'Right' },
        { 
          field: 'expectedDate', 
          headerKey: 'delivery.grid.field.expectedDate', 
          width: 150,
          render: (data) => dateRenderer(data, 'expectedDate')
        },
        { 
          field: 'actualDate', 
          headerKey: 'delivery.grid.field.actualDate', 
          width: 150,
          render: (data) => dateRenderer(data, 'actualDate')
        },
        { 
          field: 'status', 
          headerKey: 'delivery.grid.field.status', 
          width: 120,
          render: statusRenderer
        },
        { field: 'supplier', headerKey: 'delivery.grid.field.supplier', width: 150 },
        { field: 'notes', headerKey: 'delivery.grid.field.notes', width: 200 }
      ]}
      fetchData={fetchData}
      saveData={async (data: DeliveryWithId) => {
        if (data.id) {
          await DeliveryControllerService.update(data.id, data);
        } else {
          await DeliveryControllerService.create(data);
        }
      }}
      deleteData={DeliveryControllerService.delete}
      FormComponent={DeliveryFormDialog}
      entityName="delivery"
    />
  );
};

export default DeliveriesPage;
